import { useMemo, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLES, STAFF_ROLES, isStaffRole, type Role, type StaffRole } from '@pazportal/types';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useStaffInvite } from '../hooks/useStaffInvite';
import { describeJobError } from '../lib/triggerTask';
import { formatDate } from '../lib/formatters';

/**
 * Roles & access.
 *
 * Six roles, ascending privilege: viewer (customer portal, read-only),
 * support_agent, account_manager, billing_admin, admin, superadmin. The
 * five staff roles carry the `is_paznetworks_staff()` RLS bypass, so a
 * customer login (users.customer_id set) is never promoted past viewer here.
 *
 * Existing profiles use the live-session, superadmin-only role RPC. New
 * logins use a queued invitation that rechecks the actor before delivery and
 * before granting a role. Auth creates a viewer profile; the browser never
 * inserts profiles or writes global role fields directly.
 */

interface UserRow {
  id: string;
  email: string;
  full_name: string | null;
  role: Role;
  customer_id: string | null;
  customer?: { id: string; name: string } | null;
  created_at: string;
  updated_at: string;
}

const ROLE_ORDER: Role[] = ['superadmin', 'admin', 'billing_admin', 'account_manager', 'support_agent', 'viewer'];

const ROLE_DESCRIPTION: Record<Role, string> = {
  viewer: 'Customer portal, read-only',
  support_agent: 'Ticket management',
  account_manager: 'Full customer management',
  billing_admin: 'Invoices, contracts, billing',
  admin: 'All features except system configuration',
  superadmin: 'Full system access, audit log',
};

const ROLE_BADGE: Record<Role, string> = {
  superadmin: 'bg-accent-purple-soft text-accent-purple',
  admin: 'bg-primary-soft text-primary',
  billing_admin: 'bg-success-soft text-success',
  account_manager: 'bg-warning-soft text-warning',
  support_agent: 'bg-info-soft text-info',
  viewer: 'bg-surface-3 text-text-muted',
};

const INPUT =
  'w-full min-h-[44px] md:min-h-[40px] px-3 py-1.5 text-sm border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary';
const BTN =
  'inline-flex items-center justify-center min-h-[44px] md:min-h-[36px] px-3 rounded-md text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50';

function useUsers() {
  return useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('users')
        // customers.primary_contact_id also points at users, so the FK is named
        // to keep the embed unambiguous.
        .select('id, email, full_name, role, customer_id, created_at, updated_at, customer:customers!users_customer_id_fkey(id, name)')
        .order('role')
        .order('full_name');
      if (error) throw error;
      return (data ?? []) as unknown as UserRow[];
    },
    staleTime: 60 * 1000,
  });
}

export function RolesPage() {
  const { role: myRole } = useAuth();
  const canEdit = myRole === 'superadmin';
  const { data: users, isLoading, isError, refetch } = useUsers();
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<'all' | 'staff' | 'customer'>('all');

  const visible = useMemo(() => {
    let list = users ?? [];
    if (scope === 'staff') list = list.filter((u) => isStaffRole(u.role));
    if (scope === 'customer') list = list.filter((u) => !isStaffRole(u.role));
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (u) =>
          u.email.toLowerCase().includes(q) ||
          (u.full_name ?? '').toLowerCase().includes(q) ||
          (u.customer?.name ?? '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [users, search, scope]);

  const staffCount = (users ?? []).filter((u) => isStaffRole(u.role)).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-text">Roles & Access</h1>
          <p className="text-sm text-text-muted mt-0.5">
            Manage staff access separately from customer account permissions.
            {canEdit ? ' Every change requires a reason.' : ' Changing staff roles requires a superadmin.'}
          </p>
        </div>
        <div className="flex flex-wrap gap-1" role="group" aria-label="Filter by scope">
          {(['all', 'staff', 'customer'] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setScope(k)}
              aria-pressed={scope === k}
              className={`px-3 py-1.5 min-h-[44px] sm:min-h-0 text-xs font-medium rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                scope === k
                  ? 'bg-primary text-text-inverse'
                  : 'bg-surface-3 text-text-muted hover:bg-surface-strong'
              }`}
            >
              {k === 'all' ? `All (${users?.length ?? 0})` : k === 'staff' ? `PazNetworks staff (${staffCount})` : `Customer users (${(users?.length ?? 0) - staffCount})`}
            </button>
          ))}
        </div>
      </div>

      <details className="rounded-lg border border-border bg-surface">
        <summary className="cursor-pointer px-4 py-3 min-h-[44px] text-sm font-medium text-text">Role reference</summary>
        <ul className="px-4 pb-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
          {[...ROLES].map((r) => (
            <li key={r} className="flex items-center gap-2 py-1">
              <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${ROLE_BADGE[r]}`}>{r}</span>
              <span className="text-text-muted">{ROLE_DESCRIPTION[r]}</span>
            </li>
          ))}
        </ul>
      </details>

      {canEdit && <AddStaffForm onAdded={() => refetch()} />}

      <label className="block sm:w-80">
        <span className="sr-only">Search by name, email, or customer</span>
        <input
          type="search"
          placeholder="Search by name, email, or customer…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={INPUT}
        />
      </label>

      {isLoading ? (
        <div className="rounded-lg border border-border bg-surface p-6 text-sm text-text-muted" role="status">Loading users…</div>
      ) : isError ? (
        <div role="alert" className="rounded-lg border border-border bg-surface p-6 text-sm text-notification">Could not load users. Refresh to try again.</div>
      ) : visible.length === 0 ? (
        <div className="rounded-lg border border-border bg-surface p-6 text-center text-sm text-text-muted">No users match.</div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <div className="divide-y divide-divider">
            {visible.map((u) => (
              <RoleRow key={u.id} user={u} canEdit={canEdit} onChanged={() => refetch()} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AddStaffForm({ onAdded }: { onAdded: () => void }) {
  const qc = useQueryClient();
  const { role: myRole } = useAuth();
  const invite = useStaffInvite();
  const [invited, setInvited] = useState<{ email: string; role: StaffRole } | null>(null);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<StaffRole>('support_agent');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const address = email.trim().toLowerCase();
    if (!address.includes('@')) {
      setMessage({ tone: 'error', text: 'Enter a valid email address.' });
      return;
    }
    if (reason.trim().length < 5 || reason.trim().length > 1000) {
      setMessage({ tone: 'error', text: 'Enter a reason between 5 and 1,000 characters.' });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const { data: authUserId, error: lookupError } = await supabase.rpc('lookup_auth_user_id', { p_email: address });
      if (lookupError) throw new Error(lookupError.message);
      if (!authUserId) {
        // No login yet and public signup is disabled (2026-09-19), so the only
        // way in is an invite. The staff-invite task (service role) creates the
        // auth user, emails a PazNetworks invite, and sets the role.
        await invite.mutateAsync({ email: address, role, full_name: fullName.trim() || undefined, reason: reason.trim() });
        setInvited({ email: address, role });
        setEmail('');
        setFullName('');
        setReason('');
        await qc.invalidateQueries({ queryKey: ['admin-staff-users'] });
        onAdded();
        return;
      }

      const { data: existing, error: existingError } = await supabase
        .from('users')
        .select('id, role, customer_id')
        .eq('auth_user_id', authUserId)
        .maybeSingle();
      if (existingError) throw new Error(existingError.message);

      if (existing) {
        const { error: updateError } = await supabase.rpc('set_platform_role', {
          p_user_id: existing.id,
          p_role: role,
          p_reason: reason.trim(),
        });
        if (updateError) throw new Error(updateError.message);
        setMessage({
          tone: 'success',
          text: existing.role === role ? `${address} already had the ${role} role.` : `${address} is now ${role} (was ${existing.role}).`,
        });
      } else {
        await invite.mutateAsync({ email: address, role, full_name: fullName.trim() || undefined, reason: reason.trim() });
        setInvited({ email: address, role });
      }
      setEmail('');
      setFullName('');
      setReason('');
      await qc.invalidateQueries({ queryKey: ['admin-staff-users'] });
      onAdded();
    } catch (err) {
      setMessage({ tone: 'error', text: `Could not add staff member: ${describeJobError(err)}` });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-lg border border-border bg-surface p-4 space-y-3" aria-labelledby="add-staff-heading">
      <div>
        <h2 id="add-staff-heading" className="text-sm font-semibold text-text">Add or invite staff</h2>
        <p className="text-xs text-text-muted mt-0.5">
          Existing profiles receive the authorized role change immediately. Invitations are queued for processing;
          access is granted only after the request is validated.
        </p>
      </div>
      {invited && (
        <div role="status" className="flex items-start gap-3 rounded-lg border border-border bg-surface-2 p-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-soft">
            <svg className="w-5 h-5" style={{ color: 'var(--primary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-text">Staff access request queued</p>
            <p className="text-xs text-text-muted mt-0.5 break-words">
              Requested <span className="font-medium text-text">{invited.role}</span> access for{' '}
              <span className="font-medium text-text">{invited.email}</span>. Refresh the user list to verify completion.
              A queued request does not confirm email delivery or an access grant.
            </p>
            <button type="button" onClick={() => setInvited(null)} className="mt-1.5 text-xs underline text-text-muted hover:text-text">
              Dismiss
            </button>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_auto] gap-2 sm:items-end">
        <label className="block">
          <span className="block text-xs text-text-muted mb-1">Email</span>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={INPUT} placeholder="name@paznetworks.com" />
        </label>
        <label className="block">
          <span className="block text-xs text-text-muted mb-1">Full name for a new invitation (optional)</span>
          <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} className={INPUT} />
        </label>
        <label className="block">
          <span className="block text-xs text-text-muted mb-1">Role</span>
          <select value={role} onChange={(e) => setRole(e.target.value as StaffRole)} className={INPUT}>
            {[...STAFF_ROLES]
              // Only a superadmin may grant superadmin (enforced again server-side).
              .filter((r) => r !== 'superadmin' || myRole === 'superadmin')
              .map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
          </select>
        </label>
        <button type="submit" disabled={busy || reason.trim().length < 5 || reason.trim().length > 1000} className={`${BTN} bg-primary text-text-inverse hover:bg-primary-hover min-h-[44px] md:min-h-[40px]`}>
          {busy || invite.isPending ? 'Working…' : 'Add or invite'}
        </button>
      </div>
      <label className="block">
        <span className="block text-xs text-text-muted mb-1">Reason for staff access</span>
        <input type="text" required minLength={5} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} className={INPUT} />
      </label>
      {message && (
        <p role={message.tone === 'error' ? 'alert' : 'status'} className={`text-xs break-words ${message.tone === 'error' ? 'text-error' : 'text-success'}`}>
          {message.text}
        </p>
      )}
    </form>
  );
}

function RoleRow({
  user,
  canEdit,
  onChanged,
}: {
  user: UserRow;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const { user: me } = useAuth();
  const [editing, setEditing] = useState(false);
  const [role, setRole] = useState<Role>(user.role);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const isCustomerUser = user.customer_id !== null;
  // A customer login must never receive the staff RLS bypass from this row.
  const options = isCustomerUser ? (['viewer'] as Role[]) : ROLE_ORDER;
  const isSelf = me?.email?.toLowerCase() === user.email.toLowerCase();

  async function save() {
    if (reason.trim().length < 5 || reason.trim().length > 1000) {
      setMessage({ tone: 'error', text: 'Enter a reason between 5 and 1,000 characters.' });
      return;
    }
    setSaving(true);
    setMessage(null);
    const { error: updateError } = await supabase.rpc('set_platform_role', {
      p_user_id: user.id,
      p_role: role,
      p_reason: reason.trim(),
    });
    setSaving(false);
    if (updateError) {
      setMessage({ tone: 'error', text: `Could not change the role: ${updateError.message}` });
      return;
    }
    setEditing(false);
    setReason('');
    setMessage({ tone: 'success', text: `Role set to ${role}.` });
    onChanged();
  }

  return (
    <div className="bg-surface p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
      <div className="min-w-0 flex-1">
        <p className="text-sm text-text font-medium truncate">
          {user.full_name ?? user.email}
          {isSelf && <span className="ml-2 text-[11px] text-text-faint">(you)</span>}
        </p>
        <p className="text-xs text-text-muted truncate">
          {user.email}
          {user.customer?.name ? ` · ${user.customer.name}` : isStaffRole(user.role) ? ' · PazNetworks staff' : ''}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {editing ? (
          <>
            <label className="sr-only" htmlFor={`role-${user.id}`}>Role for {user.email}</label>
            <select
              id={`role-${user.id}`}
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              className="text-xs rounded border border-border bg-surface px-2 py-1 min-h-[44px] sm:min-h-[32px]"
            >
              {options.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <label className="sr-only" htmlFor={`reason-${user.id}`}>Reason for changing {user.email}'s role</label>
            <input id={`reason-${user.id}`} value={reason} onChange={(e) => setReason(e.target.value)} minLength={5} maxLength={1000} placeholder="Reason for this change" className={INPUT} />
            <button type="button" onClick={save} disabled={saving || role === user.role || reason.trim().length < 5 || reason.trim().length > 1000} className={`${BTN} bg-primary text-text-inverse`}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setRole(user.role);
                setReason('');
                setMessage(null);
              }}
              className={`${BTN} text-text-muted hover:text-text`}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${ROLE_BADGE[user.role]}`}>{user.role}</span>
            {canEdit && !isSelf && (
              <button type="button" onClick={() => setEditing(true)} className="text-xs text-primary hover:underline min-h-[44px] sm:min-h-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded">
                Edit
              </button>
            )}
          </>
        )}
      </div>
      {message && (
        <p role={message.tone === 'error' ? 'alert' : 'status'} className={`text-xs w-full sm:w-auto break-words ${message.tone === 'error' ? 'text-error' : 'text-success'}`}>
          {message.text}
        </p>
      )}
      <p className="text-[11px] text-text-faint sm:ml-2 whitespace-nowrap">Updated {formatDate(user.updated_at)}</p>
    </div>
  );
}
